"use client";

import { Suspense, useState, useEffect, useCallback, startTransition, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import SearchResultMessageAndSortFilter from "@template/Search/SearchResultMessageAndSortFilter";
import { CountApiListOfAllProperties } from "commonModule/services/search";
import DotsLoader from "components/animated/DotsLoader";
import BottomSheet, { openBottomSheet } from "components/BottomSheet";
import NewPagination from "components/custom-pagination";
import WsChevronLeftIcon from "components/icons-svg/ChevronLeftIcon";
import DownArrowIcon from "components/icons-svg/DownArrow";
import FilterIcon from "components/icons-svg/FilterIcon";
import FilterSettingsMobileIcon from "components/icons-svg/FilterSettingsMobileIcon";
import ClearFilterButton from "components/ListingMap/ClearFilterButton";
import PropertyDots from "components/ListingMap/dots-section/PropertyDots";
import ActuleSearchLocationPin from "components/ListingMap/location-icon-section/ActualSearchLocationPin";
import InnerMap from "components/ListingMap/pin-section/InnerMap";
import NotFound from "components/pages/vacation-rentals/not-found";
import { MAP_AREA, NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE } from "constant";
import { isPartnerCampaignSearchPath } from "features/partner-campaigns/campaign";
import { usePartnerCampaign } from "features/partner-campaigns/usePartnerCampaign";
import useGeoJsonCoordinates from "hooks/swr/useGeoJsonCoordinates";
import useSearchListingProperties from "hooks/swr/useSearchListingProperties";
import useSearchListingPropertiesCount from "hooks/swr/useSearchListingPropertiesCount";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { setSearchAddressResult } from "stores/save-search/saveSearchSlice";
import { markSearch, selectApiRequestType, selectIsBoundaryAutoRemoved, selectNearByProperties, selectSearchQuery, selectShowClearFilterButton, setClickClearFilter, setSearchQuery } from "stores/search/searchSlice";
import { SearchType } from "stores/search/types";
import { markUI } from "stores/ui/uiSlice";
import { canUseDOM } from "utils/dom";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import MobileSearchBoxSkeleton from "./MobileSearchBoxSkeleton";
import ModalMapLogic from "./ModalMapLogic";
import PropertyWindow from "./PropertyWindow";
import SearchAnalytics from "./SearchAnalytics";
const FilterProperty = dynamic(() => import( /* webpackChunkName: "FilterProperty" */"components/pages/vacation-rentals/MoreFilters/FilterProperty"), {
  ssr: false,
  loading: () => <>
        <button className={`hidden h-9 items-center whitespace-nowrap rounded-[10px] border border-[#EEE] bg-white px-2.5 py-2 text-sm font-medium hover:bg-secondary1000 hover:text-white lg:flex lg4:px-5`} data-nosnippet>
          Filters
          <DownArrowIcon className='ml-1 h-5 w-5' />
        </button>
        <button className='hidden min-h-[40px] min-w-[40px] items-center gap-2 p-2 md:flex lg:hidden' data-nosnippet>
          <p className='text-black text-xs font-medium leading-3'>Filters</p>
          <FilterIcon />
        </button>
        <button className='flex min-h-[40px] min-w-[40px] items-center gap-2 p-2 md:hidden' data-nosnippet>
          <p className='text-xs font-medium leading-3 text-white'>Filters</p>
          <FilterSettingsMobileIcon className='h-[22px] w-[22px]' />
        </button>
      </>
});
const ViewMap = dynamic(() => import( /* webpackChunkName: "ViewMap" */"components/ListingMap"), {
  ssr: false,
  loading: () => <div className='relative h-full w-full md:h-[calc(100dvh_-_80px)]'>
        <div className='absolute left-[46%] top-[28%] flex h-11 w-11 items-center gap-4 rounded-lg bg-[#FFFFFF99] md:top-[50%]'>
          <DotsLoader />
        </div>
      </div>
});

// do not required to load on desktop screen , that's why lazy load this
const MobileSearch = dynamic(() => import( /* webpackChunkName: "MobileSearch" */"app/vacation-rentals/[slug]/src/header/MobileSearch"), {
  loading: () => <MobileSearchBoxSkeleton />
});
const SearchResults = () => {
  const pathname = usePathname();
  const searchQuery = useAppSelector(selectSearchQuery);
  const searchApiRequestType = useAppSelector(selectApiRequestType);
  const partnerCampaign = usePartnerCampaign();
  const {
    data: propertyData,
    isLoading: isPropertiesLoading,
    mutate
  } = useSearchListingProperties();
  const {
    data: countApiResponse,
    isLoading: isPropertyCountLoading
  } = useSearchListingPropertiesCount();
  const {
    data: polygonResponse,
    isLoading: isPolygonLoading
  } = useGeoJsonCoordinates(searchQuery?.name || "");
  const count = countApiResponse?.count;
  const propertyListWithLatLng = countApiResponse?.data;
  const expandedPropertiesReturned = countApiResponse?.expandedPropertiesReturned;
  const expandedRadiusMiles = countApiResponse?.expandedRadiusMiles;
  const hasPolygonData = polygonResponse?.geojson ? true : false;
  const isSearchingNearbyProperties = useAppSelector(selectNearByProperties);
  const isBoundaryAutoRemoved = useAppSelector(selectIsBoundaryAutoRemoved);
  const showClearFilterButton = useAppSelector(selectShowClearFilterButton);
  const [previousPropertyListWithLatLng, setPreviousPropertyListWithLatLng] = useState<CountApiListOfAllProperties[] | undefined | null>(propertyListWithLatLng);
  const [isSmallerThan991] = useMediaQuery("(max-width: 991px)");
  const [isSmallerThan767] = useMediaQuery("(max-width: 767px)");
  const isSearchRoute = isPartnerCampaignSearchPath(pathname);
  const shouldShowSearchPartnerCampaignToast = !!partnerCampaign && isSearchRoute;
  const [searchPartnerCampaignToastKey, setSearchPartnerCampaignToastKey] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(true);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState<boolean>(() => {
    if (!canUseDOM()) return false;
    return window?.googleMapsLoaded === true;
  });
  useEffect(() => {
    if (!canUseDOM()) return;
    if (window?.googleMapsLoaded === true) {
      setIsGoogleMapsReady(true);
      return;
    }
    const intervalId = window.setInterval(() => {
      if (window?.googleMapsLoaded === true) {
        setIsGoogleMapsReady(true);
        window.clearInterval(intervalId);
      }
    }, 200);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);
  useEffect(() => {
    /**
      ** useEffect for automatically removing the boundary **
      If we do not get 21 properties in first API call with boundary, then we will remove the boundary
      and call the API again.
    */
    if (searchApiRequestType === SearchType.withPolygon && !isPropertyCountLoading && !isPropertiesLoading && count !== undefined && count < 21 && !searchQuery?.mapDragged && searchQuery?.withPolygon) {
      dispatch(markSearch({
        isBoundaryVisible: false,
        // we will set isBoundaryAutoRemoved to true if count is 0
        // this key is used to show the message in UI
        isBoundaryAutoRemoved: count === 0 ? true : false
      }));
      dispatch(setSearchQuery({}));
    }
  }, [count, dispatch, searchQuery?.mapDragged, searchQuery?.withPolygon, isPropertyCountLoading, isPropertiesLoading, searchApiRequestType]);
  useEffect(() => {
    if (propertyListWithLatLng && propertyListWithLatLng.length > 0 && !isPropertyCountLoading) {
      setPreviousPropertyListWithLatLng(propertyListWithLatLng);
    } else if (!isPropertyCountLoading && propertyListWithLatLng?.length === 0) {
      setPreviousPropertyListWithLatLng([]);
    }
  }, [propertyListWithLatLng, isPropertyCountLoading]);
  useEffect(() => {
    /**
      ** useEffect to search for nearby properties **
      If we do not get the property in second API call without boundary, then we will call
      the API with nearByProperties: true to get nearby properties.
    */
    if (searchApiRequestType === SearchType.withoutPolygon && !isPropertyCountLoading && !isPropertiesLoading && count !== undefined && count < 15 && !searchQuery?.mapDragged && !searchQuery?.withPolygon && !searchQuery?.nearByProperties && !showClearFilterButton) {
      dispatch(markSearch({
        isBoundaryAutoRemoved: false,
        nearByProperties: true
      }));
      dispatch(setSearchQuery({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, searchQuery?.mapDragged, searchQuery?.withPolygon, searchQuery?.nearByProperties, isPropertyCountLoading, dispatch, isPropertiesLoading, searchApiRequestType
  // showClearFilterButton, // do not add showClearFilterButton, as we do not want to trigger this useEffect when user click on clear filter button.
  ]);

  // reset UI store after the page change
  useEffect(() => {
    return () => {
      dispatch(markUI({
        currentSearchTab: null
      }));
    };
  }, [dispatch]);
  const [showMapButtonInBottomSheet, setShowMapButtonInBottomSheet] = useState(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const onBottomSheetScroll = useCallback(() => {
    const div = document.getElementById("stays_id");
    if (div && div?.getBoundingClientRect()?.top <= 50) {
      startTransition(() => {
        // to optimize performance it is wrapped in startTransition, if you don't know "startTransition" then don't touch this
        setShowMapButtonInBottomSheet(true);
      });
    } else {
      startTransition(() => {
        // to optimize performance it is wrapped in startTransition, if you don't know "startTransition" then don't touch this
        setShowMapButtonInBottomSheet(false);
      });
    }

    // Detect scroll stop
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    scrollTimeout.current = setTimeout(() => {
      const heightOfBottomSheetVisibleAboveTheFold = window.innerHeight - (div?.getBoundingClientRect()?.top || 0);
      setIsBottomSheetOpen(heightOfBottomSheetVisibleAboveTheFold > 80);
    }, 150); // 150ms delay to determine scroll stop
  }, []);
  useEffect(() => {
    if (!searchQuery?.name) return;
    if (searchQuery?.mapDragged) return;
    openBottomSheet();
    setIsBottomSheetOpen(true);
  }, [searchQuery?.name, searchQuery?.mapDragged]);
  useEffect(() => {
    return () => {
      dispatch(setSearchAddressResult({
        address_components: []
      }));
    };
  }, [dispatch]);
  useEffect(() => {
    if (!shouldShowSearchPartnerCampaignToast || !partnerCampaign) {
      setSearchPartnerCampaignToastKey(null);
      return;
    }
    const normalizedPathname = pathname?.trim() || "";
    const toastSessionKey = ["partner-campaign-search-toast", normalizedPathname, partnerCampaign.programName, partnerCampaign.promoCode].join(":");
    if (window.sessionStorage.getItem(toastSessionKey) === "1") {
      setSearchPartnerCampaignToastKey(null);
      return;
    }
    window.sessionStorage.setItem(toastSessionKey, "1");
    setSearchPartnerCampaignToastKey(toastSessionKey);
  }, [partnerCampaign, partnerCampaign?.programName, partnerCampaign?.promoCode, pathname, shouldShowSearchPartnerCampaignToast]);
  const isTheseNearByProperties = !!isSearchingNearbyProperties;
  const isShowPriceDropsAlerts = searchQuery?.name && searchQuery?.startDate && searchQuery?.endDate && !searchQuery?.isSearchingInMap && (!isSearchingNearbyProperties || propertyData?.showPDAToggle) && propertyData?.content && propertyData?.content.length !== 0;
  const partnerCampaignToastMessage = searchPartnerCampaignToastKey && partnerCampaign ? `10% off at checkout - CODE: ${partnerCampaign.promoCode}` : undefined;
  const MapComponent = useMemo(() => canUseDOM() && isGoogleMapsReady && <ViewMap isBottomSheetOpen={isBottomSheetOpen} properties={propertyData?.content || []} shouldFitBounds={isTheseNearByProperties} propertyListWithLatLng={previousPropertyListWithLatLng} isPropertiesOrPolygoneLoading={isPolygonLoading || isPropertyCountLoading || isPropertiesLoading} topPanelContent={<>
              {showClearFilterButton && <ClearFilterButton onClick={() => {
      dispatch(setClickClearFilter({
        clickClearFilter: true
      }));
    }} />}
            </>} onBottomSheetStateChange={setIsBottomSheetOpen} isDisplayAddedMoreStaysMessage={isTheseNearByProperties && propertyData?.propertyCount !== 0 && countApiResponse?.count !== 0} message={countApiResponse?.message} partnerCampaignToastKey={searchPartnerCampaignToastKey} partnerCampaignToastMessage={partnerCampaignToastMessage} isPropertyLoading={isPropertiesLoading}>
          <InnerMap properties={propertyData?.content || []} propertyListWithLatLng={propertyListWithLatLng || []} />
          {previousPropertyListWithLatLng && <PropertyDots propertyListWithLatLng={previousPropertyListWithLatLng} />}
          {(isTheseNearByProperties || isBoundaryAutoRemoved) && !isPropertiesLoading && searchQuery?.name !== MAP_AREA && <ActuleSearchLocationPin />}
        </ViewMap> || <div className='relative h-full w-full md:h-[calc(100dvh_-_80px)]'>
          <div className='absolute left-[46%] top-[28%] flex h-11 w-11 items-center gap-4 rounded-lg bg-[#FFFFFF99] md:top-[50%]'>
            <DotsLoader />
          </div>
        </div>, [isBottomSheetOpen, propertyData?.content, propertyData?.propertyCount, isTheseNearByProperties, previousPropertyListWithLatLng, isPolygonLoading, isPropertyCountLoading, isPropertiesLoading, showClearFilterButton, dispatch, countApiResponse?.count, countApiResponse?.message, isBoundaryAutoRemoved, searchQuery?.name, propertyListWithLatLng, isGoogleMapsReady, partnerCampaignToastMessage, searchPartnerCampaignToastKey]);
  return <>
      {/* mobile screen search box section */}
      {isSmallerThan767 && <div className='disable_click absolute top-0 z-[10] block h-[auto] w-full bg-primary-500 py-4 shadow-[0_4px_5px_0_rgba(0,0,0,0.15)] md:hidden md:py-2'>
          <div className={`flex items-center gap-2 pl-1 pr-4 md:px-4`}>
            <Link href='/' className='flex min-h-[40px] min-w-[40px] items-center justify-center md:hidden'>
              <WsChevronLeftIcon className='h-5 w-5 stroke-1 text-white' />
            </Link>
            <div className='flex-1'>
              <MobileSearch />
            </div>
            <FilterProperty expandedMiles={countApiResponse?.expandedRadiusMiles} />
          </div>
        </div>}

      <BottomSheet map={MapComponent} topSectionHeight={"top-[62px]"} onScroll={onBottomSheetScroll} showMapButtonInBottomSheet={showMapButtonInBottomSheet} data-sentry-element="BottomSheet" data-sentry-source-file="SearchResults.tsx">
        <div className='sticky z-40 mt-0 w-full bg-offWhite px-4 pt-[1px] md:top-0 md:z-[200] md:px-6 md:pb-4 md:shadow-[0_4px_5px_0_rgba(0,0,0,0.15)] lg:top-[78px] lg:mt-0'>
          {/* Search box and filter visible only between md and lg screen sizes (769px–991px) */}
          {isSmallerThan991 && !isSmallerThan767 && <div className='hidden gap-2 pt-4 md:flex lg:hidden '>
              <div className='flex-1'>
                <MobileSearch />
              </div>
              <FilterProperty expandedMiles={countApiResponse?.expandedRadiusMiles} />
            </div>}
          <SearchResultMessageAndSortFilter propertyData={propertyData} locationName={searchQuery?.name?.split(",")?.[0] && (searchQuery.mapDragged && !searchQuery?.withPolygon ? MAP_AREA : searchQuery.name.split(",")?.[0])} isTheseNearByProperties={isTheseNearByProperties} isPropertiesLoading={isPropertyCountLoading || isPropertiesLoading} hasPolygonData={hasPolygonData} mutate={() => void mutate?.()} data-sentry-element="SearchResultMessageAndSortFilter" data-sentry-source-file="SearchResults.tsx" />
        </div>

        <div id='stays_id'></div>
        <PropertyWindow propertyData={propertyData?.content} isTheseNearByProperties={isTheseNearByProperties} data-sentry-element="PropertyWindow" data-sentry-source-file="SearchResults.tsx" />

        <h2 className='map-show-last-rentals h-0 w-0 overflow-hidden '>
          {`Map shows premium vacation rentals in ${searchQuery?.name?.replace(/--/g, ", ")?.replace(/-/g, " ")?.split(",")?.[0] || ""}`}
        </h2>

        {/* pagination section  */}
        {propertyData?.content && propertyData?.content.length !== 0 && !!count && count > (searchQuery?.limit || NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE) && <div className={`disable_click flex w-full items-center justify-center
            ${isShowPriceDropsAlerts ? "-mt-[60px] pt-[70px] lg:pt-20" : "pt-20"}`}>
              <Suspense fallback={null}>
                <NewPagination totalPages={Math.ceil(count / NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE)} onPageChange={openBottomSheet} shouldUpdateUrl={true} count={count} />
              </Suspense>
            </div>}
        {(isSearchingNearbyProperties || searchQuery?.mapDragged) && !isPropertiesLoading && propertyData?.propertyCount === 0 && count === 0 && <NotFound />}
        <div className={`${isShowPriceDropsAlerts ? "pb-[140px]" : "pb-20 md:pb-16 "} lg4:pb-0 `} />
      </BottomSheet>
      <ModalMapLogic name={searchQuery?.name?.split(",")?.[0] || ""} data-sentry-element="ModalMapLogic" data-sentry-source-file="SearchResults.tsx">
        {MapComponent}
      </ModalMapLogic>
      {!isPropertyCountLoading && !isPropertiesLoading ? <SearchAnalytics count={count} expandedPropertiesReturned={expandedPropertiesReturned} expandedRadiusMiles={expandedRadiusMiles} withPolygon={countApiResponse?.withPolygon} multiUnitPropertyCount={countApiResponse?.multiUnitPropertyCount} totalHostBadgeCount={countApiResponse?.totalHostBadgeCount} /> : null}
    </>;
};
export default SearchResults;